/**
 * Product Zoom — fullscreen viewer.
 * Click toggles fit / 2x zoom; drag pans, wheel zooms (1x → 3x), arrows and keys navigate.
 */
import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/scrolling';
import { trapFocus } from '@theme/focus';

const ZOOM_LEVEL = 2;
const MAX_ZOOM = 3;
const MIN_ZOOM = 1;

class ProductZoomComponent extends Component {
  /** @type {{ destroy: () => void }|null} */
  #focusTrap = null;
  /** @type {HTMLElement|null} Déclencheur à refocaliser à la fermeture */
  #returnFocus = null;
  /** Galerie produit : la 3e image porte la mention IA (aussi dans la visionneuse). */
  #aiLabelThird = false;
  #syncTimer = 0;
  #observer = null;
  #images = [];
  #currentIndex = 0;
  #isZoomed = false;
  #scale = 1;
  #panX = 0;
  #panY = 0;
  #isDragging = false;
  #hasDragged = false;
  #startX = 0;
  #startY = 0;
  #startPanX = 0;
  #startPanY = 0;

  connectedCallback() {
    super.connectedCallback();

    // Plusieurs sections peuvent rendre l'overlay : on n'en garde qu'un.
    const existing = document.querySelector('product-zoom-component');
    if (existing && existing !== this) {
      this.remove();
      return;
    }

    document.addEventListener('click', this.#onTriggerClick);
    document.addEventListener('keydown', this.#onTriggerKey);
    document.addEventListener('slide-change', this.#scheduleSync, true);
    window.addEventListener('resize', this.#scheduleSync);
    this.#observer = new MutationObserver(this.#scheduleSync);
    document.querySelectorAll('product-gallery-component, [data-zoom-group]').forEach((el) =>
      this.#observer.observe(el, { childList: true, subtree: true })
    );
    this.#syncTriggers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onTriggerClick);
    document.removeEventListener('keydown', this.#onTriggerKey);
    document.removeEventListener('slide-change', this.#scheduleSync, true);
    window.removeEventListener('resize', this.#scheduleSync);
    this.#observer?.disconnect();
    this.#unbindEvents();
  }

  // ── Clavier : seule l'image visible de chaque slider est atteignable (Tab), puis Entrée / Espace ouvre ──

  #scheduleSync = () => {
    clearTimeout(this.#syncTimer);
    this.#syncTimer = setTimeout(() => this.#syncTriggers(), 150);
  };

  #syncTriggers() {
    const label = (typeof Theme !== 'undefined' && Theme.translations?.zoom_image) || 'Zoom';
    document.querySelectorAll('[data-zoom-trigger][data-zoom-src]').forEach((el) => {
      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'button');
        el.setAttribute('aria-label', label);
      }
      const slider = el.closest('.js-cv-slider');
      let visible = el.getBoundingClientRect().width > 0 && !el.closest('.cv-slider__clone');
      if (visible && slider) {
        const r = el.getBoundingClientRect();
        const box = slider.getBoundingClientRect();
        visible = r.left >= box.left - 2 && r.right <= box.right + 2;
      }
      el.tabIndex = visible ? 0 : -1;
    });
  }

  #onTriggerKey = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const trigger = event.target instanceof Element ? event.target.closest('[data-zoom-trigger]') : null;
    if (!trigger || event.target !== trigger) return;
    event.preventDefault();
    trigger.click();
  };

  // ── Open / Close ──

  #onTriggerClick = (event) => {
    const trigger = event.target.closest('[data-zoom-trigger]');
    if (!trigger) return;

    const src = trigger.dataset.zoomSrc;
    if (!src) return;

    // [data-zoom-group] permet de réutiliser la lightbox hors fiche produit.
    const gallery = trigger.closest('[data-zoom-group]') || trigger.closest('product-gallery-component');
    if (!gallery) return;

    // Only collect zoom sources from slides (not thumbnails)
    const track = gallery.querySelector('.js-slider-track');
    const zoomElements = track
      ? track.querySelectorAll('[data-zoom-src]')
      : gallery.querySelectorAll('[data-zoom-trigger][data-zoom-src]');
    const sources = [...zoomElements].filter(
      (el) => !el.closest('.cv-slider__clone') && el.dataset.zoomSrc
    );
    this.#images = sources.map((el) => el.dataset.zoomSrc);

    // La position réelle prime : un visuel manquant décale les data-index.
    const found = sources.indexOf(trigger);
    const index = found >= 0 ? found : parseInt(trigger.dataset.index ?? '0', 10);
    this.#returnFocus = trigger;
    this.#aiLabelThird = !!trigger.closest('product-gallery-component');
    this.#open(index);
  };

  #open(index = 0) {
    this.#currentIndex = index;
    this.#resetZoom();
    this.#buildThumbs();
    this.#loadImage();
    this.#updateCounter();

    this.setAttribute('aria-hidden', 'false');
    lockScroll();
    this.#bindEvents();

    // Entrance animation
    requestAnimationFrame(() => this.classList.add('is-open'));

    // Le focus entre dans la visionneuse (bouton Fermer) et y reste.
    const focusInside = (tries = 0) => {
      if (this.getAttribute('aria-hidden') !== 'false' || this.#focusTrap) return;
      const close = this.querySelector('[data-zoom-close]');
      if (close && getComputedStyle(close).visibility !== 'visible' && tries < 25) {
        setTimeout(() => focusInside(tries + 1), 20);
        return;
      }
      this.#focusTrap = trapFocus(this);
      close?.focus();
    };
    focusInside();
  }

  #buildThumbs() {
    const container = this.querySelector('[data-zoom-thumbs]');
    if (!container) return;
    container.innerHTML = '';

    this.#images.forEach((src, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'product-zoom__thumb';
      btn.dataset.thumbIndex = String(i);
      btn.setAttribute('aria-label', `Image ${i + 1}`);

      const img = document.createElement('img');
      img.src = src.split('?')[0] + '?width=120';
      img.alt = '';
      img.draggable = false;

      btn.appendChild(img);
      btn.addEventListener('click', () => {
        this.#currentIndex = i;
        this.#resetZoom();
        this.#loadImage();
        this.#updateCounter();
      });
      container.appendChild(btn);
    });

    this.#updateThumbActive();
  }

  #updateThumbActive() {
    const thumbs = this.querySelectorAll('.product-zoom__thumb');
    thumbs.forEach((t, i) => {
      t.classList.toggle('is-active', i === this.#currentIndex);
    });
  }

  #close = () => {
    this.classList.remove('is-open');
    this.#focusTrap?.destroy();
    this.#focusTrap = null;
    if (this.#returnFocus?.isConnected) this.#returnFocus.focus();
    this.#returnFocus = null;

    // Wait for exit animation
    const onEnd = () => {
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      this.#unbindEvents();
      this.removeEventListener('transitionend', onEnd);
    };

    this.addEventListener('transitionend', onEnd);

    // Fallback if no transition fires
    setTimeout(onEnd, 400);
  };

  // ── Events ──

  #bindEvents() {
    document.addEventListener('keydown', this.#onKeydown);

    const viewport = this.querySelector('[data-zoom-viewport]');
    if (!viewport) return;

    viewport.addEventListener('pointerdown', this.#onPointerDown);
    viewport.addEventListener('pointermove', this.#onPointerMove);
    viewport.addEventListener('pointerup', this.#onPointerUp);
    viewport.addEventListener('pointercancel', this.#onPointerUp);
    viewport.addEventListener('wheel', this.#onWheel, { passive: false });

    this.querySelector('[data-zoom-close]')?.addEventListener('click', this.#close);
    this.querySelector('[data-zoom-prev]')?.addEventListener('click', this.#prev);
    this.querySelector('[data-zoom-next]')?.addEventListener('click', this.#next);
  }

  #unbindEvents() {
    document.removeEventListener('keydown', this.#onKeydown);

    const viewport = this.querySelector('[data-zoom-viewport]');
    if (!viewport) return;

    viewport.removeEventListener('pointerdown', this.#onPointerDown);
    viewport.removeEventListener('pointermove', this.#onPointerMove);
    viewport.removeEventListener('pointerup', this.#onPointerUp);
    viewport.removeEventListener('pointercancel', this.#onPointerUp);
    viewport.removeEventListener('wheel', this.#onWheel);
  }

  #onKeydown = (event) => {
    switch (event.key) {
      case 'Escape': this.#close(); break;
      case 'ArrowLeft': this.#prev(); break;
      case 'ArrowRight': this.#next(); break;
    }
  };

  // ── Navigation ──

  #prev = () => {
    if (this.#currentIndex <= 0) return;
    this.#currentIndex--;
    this.#resetZoom();
    this.#loadImage();
    this.#updateCounter();
  };

  #next = () => {
    if (this.#currentIndex >= this.#images.length - 1) return;
    this.#currentIndex++;
    this.#resetZoom();
    this.#loadImage();
    this.#updateCounter();
  };

  #loadImage() {
    const img = this.querySelector('[data-zoom-image]');
    if (!img || !this.#images[this.#currentIndex]) return;

    this.querySelector('[data-zoom-viewport]')?.classList.toggle('has-ai-label', this.#aiLabelThird && this.#currentIndex === 2);
    img.style.opacity = '0';
    img.src = this.#images[this.#currentIndex];
    img.onload = () => { img.style.opacity = ''; };
  }

  #updateCounter() {
    // Hide/show arrows
    this.querySelector('[data-zoom-prev]')?.classList.toggle('is-hidden', this.#currentIndex <= 0);
    this.querySelector('[data-zoom-next]')?.classList.toggle('is-hidden', this.#currentIndex >= this.#images.length - 1);

    // Update active thumbnail
    this.#updateThumbActive();
  }

  // ── Zoom / Pan ──

  #resetZoom() {
    this.#scale = 1;
    this.#panX = 0;
    this.#panY = 0;
    this.#isZoomed = false;
    this.#applyTransform(false);
    this.#updateCursorState();
  }

  #toggleZoom(clientX, clientY) {
    if (this.#isZoomed) {
      // Zoom out
      this.#scale = 1;
      this.#panX = 0;
      this.#panY = 0;
      this.#isZoomed = false;
    } else {
      // Zoom in — center on click point
      this.#scale = ZOOM_LEVEL;
      this.#isZoomed = true;

      const viewport = this.querySelector('[data-zoom-viewport]');
      if (viewport) {
        const rect = viewport.getBoundingClientRect();
        const offsetX = clientX - rect.left - rect.width / 2;
        const offsetY = clientY - rect.top - rect.height / 2;
        this.#panX = -offsetX * (ZOOM_LEVEL - 1);
        this.#panY = -offsetY * (ZOOM_LEVEL - 1);
        this.#clampPan();
      }
    }

    this.#applyTransform(true);
    this.#updateCursorState();
  }

  #applyTransform(animate = false) {
    const img = this.querySelector('[data-zoom-image]');
    if (!img) return;

    img.style.transition = animate ? 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)' : 'none';
    img.style.transform = `translate(${this.#panX}px, ${this.#panY}px) scale(${this.#scale})`;
  }

  #updateCursorState() {
    const viewport = this.querySelector('[data-zoom-viewport]');
    if (!viewport) return;

    if (this.#isZoomed) {
      viewport.dataset.cursor = 'grab';
    } else {
      viewport.dataset.cursor = 'zoom-in';
    }
  }

  #clampPan() {
    if (this.#scale <= 1) {
      this.#panX = 0;
      this.#panY = 0;
      return;
    }

    const viewport = this.querySelector('[data-zoom-viewport]');
    const img = this.querySelector('[data-zoom-image]');
    if (!viewport || !img) return;

    const vw = viewport.clientWidth;
    const vh = viewport.clientHeight;
    const iw = img.naturalWidth || vw;
    const ih = img.naturalHeight || vh;

    const fitRatio = Math.min(vw / iw, vh / ih);
    const displayW = iw * fitRatio * this.#scale;
    const displayH = ih * fitRatio * this.#scale;

    const maxX = Math.max(0, (displayW - vw) / 2);
    const maxY = Math.max(0, (displayH - vh) / 2);

    this.#panX = Math.max(-maxX, Math.min(maxX, this.#panX));
    this.#panY = Math.max(-maxY, Math.min(maxY, this.#panY));
  }

  // ── Pointer Events ──

  #onPointerDown = (event) => {
    if (event.button !== 0) return;

    this.#isDragging = true;
    this.#hasDragged = false;
    this.#startX = event.clientX;
    this.#startY = event.clientY;
    this.#startPanX = this.#panX;
    this.#startPanY = this.#panY;

    const viewport = this.querySelector('[data-zoom-viewport]');
    if (viewport && this.#isZoomed) viewport.dataset.cursor = 'grabbing';

    event.target.setPointerCapture?.(event.pointerId);
  };

  #onPointerMove = (event) => {
    if (!this.#isDragging || !this.#isZoomed) return;

    const dx = event.clientX - this.#startX;
    const dy = event.clientY - this.#startY;

    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      this.#hasDragged = true;
    }

    this.#panX = this.#startPanX + dx;
    this.#panY = this.#startPanY + dy;
    this.#clampPan();
    this.#applyTransform(false);
  };

  #onPointerUp = (event) => {
    if (!this.#isDragging) return;
    this.#isDragging = false;

    const viewport = this.querySelector('[data-zoom-viewport]');
    if (viewport && this.#isZoomed) viewport.dataset.cursor = 'grab';

    // If no drag happened, treat as a click → toggle zoom
    if (!this.#hasDragged) {
      this.#toggleZoom(event.clientX, event.clientY);
    }
  };

  #onWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.15 : 0.15;
    const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.#scale + delta));

    if (newScale === this.#scale) return;

    this.#scale = newScale;
    this.#isZoomed = this.#scale > 1;
    this.#clampPan();
    this.#applyTransform(false);
    this.#updateCursorState();
  };
}

if (!customElements.get('product-zoom-component')) {
  customElements.define('product-zoom-component', ProductZoomComponent);
}